import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@ouropix/ui';
import { Progress } from '@ouropix/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ouropix/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ouropix/ui';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  ArrowLeft,
  BarChart3,
  DollarSign,
  Loader2,
  ShoppingCart,
  TrendingDown,
  TrendingUp,
  Users,
} from 'lucide-react';
import AdminPageHeader from '@/components/layout/PageHeader';
import { GAME_STATUS_LABELS, GAME_STATUS_VARIANT } from '@/lib/game-enums';

export const Route = createFileRoute('/_admin/jogos/$id/dashboard')({
  component: GameDashboardPage,
});

type RangeKey = '24h' | '7d' | '30d' | 'all';

interface DashboardData {
  game: {
    id: string;
    title: string;
    status: string;
    icon_url: string | null;
    modality: { name: string; icon: string | null } | null;
    structure_id: string | null;
    pool_id: string | null;
    numbers_consumed: number;
    numbers_total: number;
  };
  kpis: {
    purchases: number;
    purchases_delta_pct: number | null;
    revenue_brl: number;
    revenue_delta_pct: number | null;
    prizes_paid_brl: number;
    unique_users: number;
    unique_users_delta_pct: number | null;
  };
  chart: Array<{ date: string; full_date: string; revenue: number; purchases: number }>;
  top_winners: Array<{
    user_name_masked: string;
    total_won_brl: number;
    wins_count: number;
  }>;
}

const RANGE_LABELS: Record<RangeKey, string> = {
  '24h': 'Últimas 24h',
  '7d': 'Últimos 7 dias',
  '30d': 'Últimos 30 dias',
  'all': 'Todo o período',
};

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function DeltaBadge({ pct }: { pct: number | null }) {
  if (pct === null) return null;
  const positive = pct >= 0;
  return (
    <span className={`flex items-center gap-0.5 text-xs font-medium ${positive ? 'text-green-500' : 'text-red-500'}`}>
      {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

function ChartTooltip({ active, payload }: { active?: boolean; payload?: Array<{ color: string; name: string; value: number }> }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
      {payload.map((p, i) => (
        <p key={i} className="text-xs" style={{ color: p.color }}>
          {p.name}: {p.name === 'Receita' ? formatCurrency(p.value) : p.value.toLocaleString('pt-BR')}
        </p>
      ))}
    </div>
  );
}

function GameDashboardPage() {
  const { id } = Route.useParams();
  const [range, setRange] = useState<RangeKey>('7d');

  const { data, isLoading } = useQuery<DashboardData>({
    queryKey: ['admin-game-dashboard', id, range],
    queryFn: async () => {
      const r = await api.get(`/admin/games/${id}/dashboard`, { params: { range } });
      return r.data?.data ?? r.data;
    },
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const game = data?.game;
  const kpis = data?.kpis;
  const chart = data?.chart ?? [];
  const topWinners = data?.top_winners ?? [];
  const poolPct = game && game.numbers_total > 0
    ? (game.numbers_consumed / game.numbers_total) * 100
    : 0;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={BarChart3}
        title={game?.title ?? 'Dashboard do Jogo'}
        description={game ? `Modalidade: ${game.modality?.name ?? '—'}` : undefined}
        actions={
          <Button variant="outline" size="sm" asChild>
            <Link to="/jogos/$id" params={{ id }}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar ao jogo
            </Link>
          </Button>
        }
      />

      {game && (
        <div className="flex items-center gap-3">
          {game.icon_url && (
            <img src={game.icon_url} alt={game.title} className="h-12 w-12 rounded-lg object-cover" />
          )}
          <Badge variant={GAME_STATUS_VARIANT[game.status as keyof typeof GAME_STATUS_VARIANT] ?? 'outline'}>
            {GAME_STATUS_LABELS[game.status as keyof typeof GAME_STATUS_LABELS] ?? game.status}
          </Badge>
        </div>
      )}

      {/* Range filter */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Período:</span>
        <Select value={range} onValueChange={(v) => setRange(v as RangeKey)}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(RANGE_LABELS) as RangeKey[]).map((k) => (
              <SelectItem key={k} value={k}>{RANGE_LABELS[k]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* KPI cards */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <ShoppingCart className="h-4 w-4" />Compras
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(kpis?.purchases ?? 0).toLocaleString('pt-BR')}</div>
            <DeltaBadge pct={kpis?.purchases_delta_pct ?? null} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <DollarSign className="h-4 w-4" />Receita
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{formatCurrency(kpis?.revenue_brl ?? 0)}</div>
            <DeltaBadge pct={kpis?.revenue_delta_pct ?? null} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <DollarSign className="h-4 w-4" />Prêmios Pagos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-500">{formatCurrency(kpis?.prizes_paid_brl ?? 0)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Users className="h-4 w-4" />Usuários Únicos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(kpis?.unique_users ?? 0).toLocaleString('pt-BR')}</div>
            <DeltaBadge pct={kpis?.unique_users_delta_pct ?? null} />
          </CardContent>
        </Card>
      </div>

      {/* Pool status */}
      {game && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Status do Pool</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Números consumidos</span>
              <span className="font-medium">
                {game.numbers_consumed.toLocaleString('pt-BR')} / {game.numbers_total.toLocaleString('pt-BR')}
                {' '}({poolPct.toFixed(1)}%)
              </span>
            </div>
            <Progress value={poolPct} className="h-3" />
            {game.structure_id && (
              <div className="pt-1">
                <Button variant="outline" size="sm" asChild>
                  <Link
                    to="/lotep/estruturas/$structureId/pool"
                    params={{ structureId: game.structure_id }}
                    search={game.pool_id ? { pool_id: game.pool_id } : undefined}
                  >
                    Ver números
                  </Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sales chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Vendas / Receita ao longo do tempo</CardTitle>
        </CardHeader>
        <CardContent>
          {chart.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Sem dados para o período selecionado.</p>
          ) : (
            <ResponsiveContainer width="100%" height={288}>
              <AreaChart data={chart} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorRevDash" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorPurchDash" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} className="text-muted-foreground" />
                <YAxis tick={{ fontSize: 10 }} tickLine={false} width={60} className="text-muted-foreground" />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="revenue" name="Receita" stroke="#10b981" strokeWidth={2} fill="url(#colorRevDash)" />
                <Area type="monotone" dataKey="purchases" name="Compras" stroke="#6366f1" strokeWidth={2} fill="url(#colorPurchDash)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Top 10 winners */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Top 10 Ganhadores</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Usuário</TableHead>
                <TableHead>Total Ganho</TableHead>
                <TableHead>Vitórias</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topWinners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                    Nenhum ganhador no período.
                  </TableCell>
                </TableRow>
              ) : (
                topWinners.map((w, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-muted-foreground font-medium">{i + 1}</TableCell>
                    <TableCell className="font-mono text-sm">{w.user_name_masked}</TableCell>
                    <TableCell className="text-green-500 font-medium">{formatCurrency(w.total_won_brl)}</TableCell>
                    <TableCell>{w.wins_count}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
